import React, {memo} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {default as StarRating} from '@homeaway/react-star-rating';
import message from 'framework/modules/message';
import Config from 'config';
import './review-ratings.less';
import egtnlUtil from '../../helpers/egtnl-util';

const getReviewRoundedLocale = (
    roundedAverage,
    badge,
    isPropertyRatingChangeEnabled
) => {
    if (badge && !isPropertyRatingChangeEnabled) {
        return badge.name;
    }

    const newRoundedAverage = isPropertyRatingChangeEnabled
        ? roundedAverage * 2
        : roundedAverage;

    // DE_de will be replaced to DE-de
    // 4.9 will be replaced to 4,9 for DE-de
    let localizedRoundedAverage = newRoundedAverage.toLocaleString(
        Config.locale && Config.locale.replace('_', '-')
    );

    return isPropertyRatingChangeEnabled
        ? `${localizedRoundedAverage}/10`
        : `${localizedRoundedAverage}/5`;
};

const getSuperlativeMessage = (roundedAverage, badge) => {
    if (badge) {
        return badge.helpText;
    }

    if (roundedAverage === 5) {
        return message.read('tc.rating.superlative.exceptional');
    }

    if (roundedAverage >= 4.8) {
        return message.read('tc.rating.superlative.wonderful');
    }

    if (roundedAverage >= 4.3) {
        return message.read('tc.rating.superlative.excellent');
    }

    if (roundedAverage >= 4.0) {
        return message.read('tc.rating.superlative.very.good');
    }

    if (roundedAverage >= 3.7) {
        return message.read('tc.rating.superlative.good');
    }

    return null;
};

const SuperlativeRating = ({
    averageRating,
    reviewCount,
    reviewScoreSplitBadges,
}) => {
    const isPropertyRatingChangeEnabled =
        egtnlUtil.isPropertyRatingChangeEnabled();
    const badge = reviewScoreSplitBadges && reviewScoreSplitBadges[0];
    const roundedAverage = _.round(averageRating, 1);
    const reviewRoundedLocale = getReviewRoundedLocale(
        roundedAverage,
        badge,
        isPropertyRatingChangeEnabled
    );
    const superlativeMessage = getSuperlativeMessage(roundedAverage, badge);

    return (
        <div className="superlative-rating">
            {isPropertyRatingChangeEnabled && (
                <div className="superlative-rating__new-message">
                    <b>
                        {reviewRoundedLocale}{' '}
                        {superlativeMessage && superlativeMessage.slice(0, -1)}
                    </b>{' '}
                    {`(${reviewCount} ${message.read(
                        'traveler.checkout.reviews'
                    )})`}
                </div>
            )}
            {!isPropertyRatingChangeEnabled && (
                <div>
                    <div className="superlative-rating__average">
                        <b>{reviewRoundedLocale}</b>
                    </div>
                    <div>
                        <div className="superlative-rating__message">
                            {superlativeMessage &&
                                `"${superlativeMessage.trim()}"`}
                        </div>
                        <div className="nowrap small-rating">
                            <StarRating rating={averageRating} size="12" />
                            {` (${reviewCount})`}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

SuperlativeRating.propTypes = {
    averageRating: PropTypes.number,
    reviewCount: PropTypes.number,
    reviewScoreSplitBadges: PropTypes.array,
};
SuperlativeRating.defaultProps = {
    averageRating: 0,
    reviewCount: 0,
    reviewScoreSplitBadges: [],
};

export default memo(SuperlativeRating);
